import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  Paper,
  Chip,
  Switch,
  FormControlLabel,
  Slider,
  Select,
  InputLabel,
  FormControl,
  Grid,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Refresh,
  MoreVert,
  Add,
  Delete,
  Visibility,
  VisibilityOff,
  AccountTree,
  Storage,
  Router,
  DevicesOther,
  Security,
  Cloud,
  Dns,
  SettingsEthernet,
  Hub,
  Lan,
  Wifi,
  DeviceHub,
  CloudQueue,
  DnsOutlined,
  RouterOutlined,
  DeviceHubOutlined,
  CloudOutlined,
} from '@mui/icons-material';
import { useActivity } from '../../contexts/ActivityContext';
import { useReactFlow, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';

// Custom node types
const nodeTypes = {
  router: ({ data }) => (
    <Tooltip title={data.ip || 'Unknown IP'}>
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(145deg, #1e1e2d, #252538)',
          boxShadow: '5px 5px 10px #0f0f17, -5px -5px 10px #2d2d45',
          border: `2px solid ${data.status === 'online' ? '#4caf50' : '#f44336'}`,
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'scale(1.05)',
            transition: 'transform 0.2s ease-in-out',
          },
        }}
      >
        <RouterOutlined sx={{ fontSize: 32, color: data.status === 'online' ? '#4caf50' : '#f44336' }} />
        <Typography variant="caption" sx={{ mt: 0.5, color: 'text.primary', fontWeight: 600 }}>
          {data.label}
        </Typography>
        {data.status === 'online' && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 4,
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#4caf50',
              boxShadow: '0 0 10px #4caf50',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 0.6 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.6 },
              },
            }}
          />
        )}
      </Box>
    </Tooltip>
  ),
  // Add more node types (switch, server, etc.) with similar structure
};

const NetworkMapper = () => {
  const theme = useTheme();
  const { updateActivity } = useActivity();
  const [isScanning, setIsScanning] = useState(false);
  const [target, setTarget] = useState('192.168.1.0/24');
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [layout, setLayout] = useState('hierarchical');
  const [showLabels, setShowLabels] = useState(true);
  const [zoom, setZoom] = useState(100);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  
  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useReactFlow();

  // Simulate network scan (in a real app, this would be an API call)
  const scanNetwork = async () => {
    setIsScanning(true);
    updateActivity('Network Mapper', `Started scanning network: ${target}`);
    
    // Simulate scanning delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock data - in a real app, this would come from an API
    const mockNodes = [
      {
        id: '1',
        type: 'router',
        position: { x: 250, y: 50 },
        data: { 
          label: 'Router', 
          ip: '192.168.1.1',
          mac: '00:1A:2B:3C:4D:5E',
          vendor: 'Cisco',
          status: 'online',
          lastSeen: new Date().toISOString(),
        },
      },
      // Add more mock nodes...
    ];
    
    const mockEdges = [
      // Define connections between nodes
    ];
    
    setNodes(mockNodes);
    setEdges(mockEdges);
    setIsScanning(false);
    updateActivity('Network Mapper', `Completed scan of ${target}`, { nodes: mockNodes.length });
  };

  const handleLayoutChange = (newLayout) => {
    setLayout(newLayout);
    // In a real implementation, we'd update the layout algorithm here
  };

  const handleNodeClick = (event, node) => {
    setSelectedNode(node);
  };

  const handleContextMenu = (event, node) => {
    event.preventDefault();
    setSelectedNode(node);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleZoom = (event, newValue) => {
    setZoom(newValue);
    const scale = newValue / 100;
    reactFlowInstance.setViewport({ x: 0, y: 0, zoom: scale });
  };

  return (
    <Box sx={{ height: 'calc(100vh - 200px)' }}>
      <Card variant="outlined" sx={{ mb: 2, background: 'rgba(30, 30, 45, 0.7)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              label="Network Range"
              variant="outlined"
              size="small"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={isScanning}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={isScanning ? <Stop /> : <PlayArrow />}
              onClick={isScanning ? () => setIsScanning(false) : scanNetwork}
              disabled={!target}
            >
              {isScanning ? 'Stop Scan' : 'Start Scan'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setNodes([]);
                setEdges([]);
                setSelectedNode(null);
              }}
              disabled={isScanning}
            >
              Clear
            </Button>
            
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 1 }}>
              <Tooltip title="Layout">
                <FormControl size="small" variant="outlined" sx={{ minWidth: 120 }}>
                  <Select
                    value={layout}
                    onChange={(e) => handleLayoutChange(e.target.value)}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Layout' }}
                    sx={{
                      color: 'text.primary',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.23)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value="hierarchical">Hierarchical</MenuItem>
                    <MenuItem value="force">Force-Directed</MenuItem>
                    <MenuItem value="grid">Grid</MenuItem>
                    <MenuItem value="circular">Circular</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
              
              <Tooltip title={showLabels ? 'Hide Labels' : 'Show Labels'}>
                <IconButton
                  onClick={() => setShowLabels(!showLabels)}
                  color={showLabels ? 'primary' : 'default'}
                >
                  {showLabels ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </Tooltip>
              
              <Box sx={{ width: 120, px: 2 }}>
                <Slider
                  value={zoom}
                  onChange={handleZoom}
                  min={25}
                  max={200}
                  step={25}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                  aria-labelledby="zoom-slider"
                />
              </Box>
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              icon={<RouterOutlined />}
              label={`Routers: ${nodes.filter(n => n.type === 'router').length}`}
              variant="outlined"
              color="primary"
            />
            <Chip
              icon={<DeviceHubOutlined />}
              label={`Switches: ${nodes.filter(n => n.type === 'switch').length}`}
              variant="outlined"
              color="secondary"
            />
            <Chip
              icon={<DnsOutlined />}
              label={`Servers: ${nodes.filter(n => n.type === 'server').length}`}
              variant="outlined"
              color="success"
            />
            <Chip
              icon={<DevicesOther />}
              label={`Devices: ${nodes.filter(n => n.type === 'device').length}`}
              variant="outlined"
              color="info"
            />
          </Box>
        </CardContent>
      </Card>
      
      <Card variant="outlined" sx={{ height: 'calc(100% - 120px)', position: 'relative', background: 'transparent' }}>
        <Box
          ref={reactFlowWrapper}
          sx={{
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle at center, #1a1a2e 0%, #16213e 100%)',
            borderRadius: 1,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              pointerEvents: 'none',
            },
          }}
        >
          {nodes.length === 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <AccountTree sx={{ fontSize: 64, opacity: 0.5, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Network Devices Found
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                Enter a network range and click "Start Scan" to discover devices
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrow />}
                onClick={scanNetwork}
                disabled={isScanning || !target}
              >
                Start Network Scan
              </Button>
            </Box>
          )}
          
          {/* React Flow will be rendered here */}
          
          {selectedNode && (
            <Card
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                width: 300,
                zIndex: 10,
                background: 'rgba(26, 26, 46, 0.9)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.palette.primary.main}`,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selectedNode.type === 'router' && <Router fontSize="small" />}
                    {selectedNode.type === 'switch' && <Hub fontSize="small" />}
                    {selectedNode.type === 'server' && <Storage fontSize="small" />}
                    {selectedNode.type === 'device' && <DevicesOther fontSize="small" />}
                    {selectedNode.data.label}
                  </Typography>
                  <IconButton size="small" onClick={() => setSelectedNode(null)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ '& > *': { mb: 0.5 } }}>
                  <Typography variant="body2">
                    <strong>IP:</strong> {selectedNode.data.ip || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>MAC:</strong> {selectedNode.data.mac || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> 
                    <Chip 
                      label={selectedNode.data.status || 'unknown'} 
                      size="small" 
                      color={selectedNode.data.status === 'online' ? 'success' : 'error'}
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  </Typography>
                  {selectedNode.data.vendor && (
                    <Typography variant="body2">
                      <strong>Vendor:</strong> {selectedNode.data.vendor}
                    </Typography>
                  )}
                  {selectedNode.data.lastSeen && (
                    <Typography variant="body2">
                      <strong>Last Seen:</strong> {new Date(selectedNode.data.lastSeen).toLocaleString()}
                    </Typography>
                  )}
                </Box>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  size="small" 
                  sx={{ mt: 2 }}
                  onClick={() => {
                    // Action to show more details
                    updateActivity('Network Mapper', `Viewing details for ${selectedNode.data.label}`);
                  }}
                >
                  View Full Details
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>
      </Card>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
      >
        <MenuItem>
          <Visibility fontSize="small" sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem>
          <SettingsEthernet fontSize="small" sx={{ mr: 1 }} /> Trace Route
        </MenuItem>
        <MenuItem>
          <Dns fontSize="small" sx={{ mr: 1 }} /> DNS Lookup
        </MenuItem>
        <Divider />
        <MenuItem sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Remove
        </MenuItem>
      </Menu>
    </Box>
  );
};

// Wrap with ReactFlowProvider for proper context
const NetworkMapperWithProvider = (props) => (
  <ReactFlowProvider>
    <NetworkMapper {...props} />
  </ReactFlowProvider>
);

export default NetworkMapperWithProvider;
